import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsMenusV1ItemLabel from './restaurants-menus-v1-item-label-item-labels.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Label {
  /**
   * Item label ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item label is updated. To prevent conflicting changes, the current revision must be passed when updating the item label. Ignored when creating a item label.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item label was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item label was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item label name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Item label icon. */
  icon?: string;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ExternalReferenceInfo {
  /**
   * ID of the external integrator app\system associated with this object.
   * @format GUID
   */
  externalAppId?: string | null;
  /**
   * Reference to an external resource ID. Used to link this object to a specific entity in an external system.
   * @maxLength 100
   */
  externalEntityId?: string | null;
  /** Last time this item label was synced with the external app. */
  lastSyncDate?: Date | null;
  /** Whether the connection to the external system is currently active. */
  connected?: boolean | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateLabelRequest {
  /** Item label details. */
  label: Label;
}

export interface CreateLabelResponse {
  /** Item label. */
  label?: Label;
}

export interface GetLabelRequest {
  /**
   * Item label ID.
   * @format GUID
   */
  labelId: string;
}

export interface GetLabelResponse {
  /** Item label. */
  label?: Label;
}

export interface ListLabelsRequest {}

export interface ListLabelsResponse {
  /** Retrieved item labels. */
  labels?: Label[];
}

export interface QueryLabelsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 500
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryLabelsResponse {
  /** Retrieved item labels. */
  labels?: Label[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface CountLabelsRequest {
  /** Filter for counting labels. */
  filter?: Record<string, any> | null;
}

export interface CountLabelsResponse {
  /** Counted labels. */
  count?: number;
}

export interface UpdateLabelRequest {
  /** Item label to update. */
  label: Label;
}

export interface UpdateLabelResponse {
  /** Updated item label. */
  label?: Label;
}

export interface DeleteLabelRequest {
  /**
   * ID of the item label.
   * @format GUID
   */
  labelId: string;
}

export interface DeleteLabelResponse {}

export interface BulkDeleteLabelsRequest {
  /**
   * Item Label IDs.
   * @format GUID
   * @maxSize 200
   */
  ids: string[];
}

export interface BulkDeleteLabelsResponse {
  /** Information about the deleted labels. */
  results?: BulkDeleteLabelsResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteLabelsResult {
  /** Metadata for labels deletion. */
  itemMetadata?: ItemMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface CloneLabelsRequest {
  /**
   * The MetaSiteId to clone from.
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CloneLabelsResponse {}

export interface SyncContentToMultilingualRequest {
  /**
   * MetaSite ID
   * @format GUID
   */
  metaSiteId?: string;
}

export interface SyncContentToMultilingualResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CountLabelsApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ItemLabelCreatedEnvelope {
  entity: Label;
  metadata: EventMetadata;
}

/**
 * Triggered when an item label is created.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.LABEL_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_label_created
 * @serviceIdentifier com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels
 * @slug created
 * @documentationMaturity preview
 */
export declare function onItemLabelCreated(
  handler: (event: ItemLabelCreatedEnvelope) => void | Promise<void>
): void;

export interface ItemLabelDeletedEnvelope {
  metadata: EventMetadata;
}

/** @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.LABEL_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_label_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onItemLabelDeleted(
  handler: (event: ItemLabelDeletedEnvelope) => void | Promise<void>
): void;

export interface ItemLabelUpdatedEnvelope {
  entity: Label;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an item label is updated.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.LABEL_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.item_label_updated
 * @serviceIdentifier com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onItemLabelUpdated(
  handler: (event: ItemLabelUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates an item label.
 * @param label - Item label details.
 * @public
 * @documentationMaturity preview
 * @requiredField label
 * @permissionId RESTAURANTS.LABEL_CREATE
 * @applicableIdentity APP
 * @returns Item label.
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.CreateLabel
 */
export async function createLabel(
  label: Label
): Promise<NonNullablePaths<Label, `name`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ label: label }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'label.icon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.createLabel(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'label.icon' }],
        },
      ])
    )?.label!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { label: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['label']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Labels API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves an item label by ID.
 * @param labelId - Item label ID.
 * @public
 * @documentationMaturity preview
 * @requiredField labelId
 * @permissionId RESTAURANTS.LABEL_READ
 * @applicableIdentity APP
 * @returns Item label.
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.GetLabel
 */
export async function getLabel(
  labelId: string
): Promise<NonNullablePaths<Label, `name`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ labelId: labelId });

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.getLabel(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'label.icon' }],
        },
      ])
    )?.label!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { labelId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['labelId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 item labels.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.LABEL_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.ListLabels
 */
export async function listLabels(): Promise<
  NonNullablePaths<ListLabelsResponse, `labels` | `labels.${number}.name`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.listLabels(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'labels.icon' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of item labels.
 *
 * The `queryLabels()` function builds a query to retrieve a list of item labels and returns a `LabelsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-labels/labels-query-builder/find) function.
 *
 * You can refine the query by chaining `LabelsQueryBuilder` functions onto the query. `LabelsQueryBuilder` functions enable you to filter, sort, and control the results that `queryLabels()` returns.
 *
 * `queryLabels()` runs with the following `LabelsQueryBuilder` defaults, which you can override:
 *
 * * [`limit(100)`](/item-labels/labels-query-builder/limit)
 *
 * The following `LabelsQueryBuilder` functions are supported for `queryLabels()`. For a full description of the item label object, see the object returned for the [`items`](/item-labels/labels-query-result/items) property in `LabelsQueryResult`.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.LABEL_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.QueryLabels
 */
export function queryLabels(): LabelsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Label, 'CURSOR', QueryLabelsRequest, QueryLabelsResponse>(
    {
      func: async (payload: QueryLabelsRequest) => {
        const reqOpts =
          ambassadorWixRestaurantsMenusV1ItemLabel.queryLabels(payload);

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryLabelsRequest['query']) => {
        const args = [query, {}] as [QueryLabelsRequest['query'], {}];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryLabelsResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [
            {
              transformFn: transformRESTImageToSDKImage,
              paths: [{ path: 'labels.icon' }],
            },
          ])
        );

        return {
          items: transformedData?.labels,
          pagingMetadata: transformedData?.pagingMetadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'CURSOR',
      transformationPaths: {},
    }
  );
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface LabelsQueryResult extends QueryCursorResult {
  items: Label[];
  query: LabelsQueryBuilder;
  next: () => Promise<LabelsQueryResult>;
  prev: () => Promise<LabelsQueryResult>;
}

export interface LabelsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_createdDate' | '_updatedDate' | 'name',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => LabelsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (propertyName: 'name', value: string) => LabelsQueryBuilder;
  /** @documentationMaturity preview */
  in: (propertyName: '_id' | 'name', value: any) => LabelsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (propertyName: 'icon', value: boolean) => LabelsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => LabelsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => LabelsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<LabelsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.QueryLabels
 * @requiredField query
 */
export async function typedQueryLabels(
  query: LabelQuery
): Promise<
  NonNullablePaths<QueryLabelsResponse, `labels` | `labels.${number}.name`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.queryLabels(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'labels.icon' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LabelQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'NONE';
    },
    {
      fields: ['icon'];
      operators: ['$exists'];
      sort: 'NONE';
    },
    {
      fields: ['name'];
      operators: ['$eq', '$in', '$ne', '$nin', '$startsWith'];
      sort: 'NONE';
    },
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: ['$eq', '$gt', '$gte', '$lt', '$lte', '$ne'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Label, LabelQuerySpec>;
export type LabelQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 500 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Label, LabelQuerySpec, LabelQuery>(),
  },
};

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves the number of labels that match a specified filter.
 *
 * If a filter isn't passed in the request, the endpoint returns the count of all labels.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.LABEL_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.CountLabels
 */
export async function countLabels(options?: CountLabelsOptions): Promise<
  NonNullablePaths<CountLabelsResponse, `count`, 2> & {
    __applicationErrorsType?: CountLabelsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.countLabels(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountLabelsOptions {
  /** Filter for counting labels. */
  filter?: Record<string, any> | null;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates an item Label.
 *
 * Each time an item label is updated, its revision increments by 1. The existing revision must be included when updating the item labels. This ensures you're working with the latest item labels information, and it prevents unintended overwrites.
 * @param _id - Item label ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField label
 * @requiredField label.revision
 * @permissionId RESTAURANTS.LABEL_UPDATE
 * @applicableIdentity APP
 * @returns Updated item label.
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.UpdateLabel
 */
export async function updateLabel(
  _id: string,
  label: NonNullablePaths<UpdateLabel, `revision`, 2>
): Promise<NonNullablePaths<Label, `name`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ label: { ...label, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'label.icon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.updateLabel(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'label.icon' }],
        },
      ])
    )?.label!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { label: '$[1]' },
        explicitPathsToArguments: { 'label.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'label']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateLabel {
  /**
   * Item label ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item label is updated. To prevent conflicting changes, the current revision must be passed when updating the item label. Ignored when creating a item label.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item label was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the item label was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Item label name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string;
  /** Item label icon. */
  icon?: string;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes an item label.
 * @param labelId - ID of the item label.
 * @public
 * @documentationMaturity preview
 * @requiredField labelId
 * @permissionId RESTAURANTS.LABEL_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.DeleteLabel
 */
export async function deleteLabel(labelId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ labelId: labelId });

  const reqOpts = ambassadorWixRestaurantsMenusV1ItemLabel.deleteLabel(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { labelId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['labelId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Label API only works with the Wix Restaurants Menus app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes multiple item labels at once.
 * @param ids - Item Label IDs.
 * @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId RESTAURANTS.LABEL_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_item_labels.v1.RestaurantsMenusItemLabels.BulkDeleteLabels
 */
export async function bulkDeleteLabels(
  ids: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteLabelsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1ItemLabel.bulkDeleteLabels(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
